<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getEditorialChildLocationNearbySearchResults, getEditorialLocationNearbySearchResults, type GetEditorialNearbySearchResultsProjectResponse } from '@/api';
import SearchResult from '@/views/search/SearchResult.vue';
import SectionHeader from '@/components/SectionHeader.vue';
import { useHead, type ResolvableScript } from '@unhead/vue';
import { useRoute } from 'vue-router';
import { getCanonicalUrl } from '@/utilities';
import { useProjectJsonSchema } from '@/utilities/json-ld.ts';
import { useSsgState } from '@/utilities/ssg-state.ts';

const props = defineProps<{
  categorySlug?: string;
  parentLocationSlug?: string;
  locationSlug?: string;
  title?: string;
  priceTypeId?: number | null;
}>();

const { t } = useI18n();
const route = useRoute();
const canonicalUrl = computed(() => getCanonicalUrl(route.path));
const { generateProjectJsonSchema, getProjectCanonicalUrl, getProjectListingId } = useProjectJsonSchema();
const loading = ref(true);
const searchResults = ref<GetEditorialNearbySearchResultsProjectResponse[]>([]);
const hasResults = computed(() => searchResults.value.length > 0);
const projectJsonLd = computed(() => searchResults.value.map((project) => generateProjectJsonSchema(project)));
const itemList = computed(() => ({
  '@type': 'ItemList',
  '@id': `${canonicalUrl.value}#nearby-results`,
  name: t('views.editorial.nearby-search-results.title'),
  numberOfItems: searchResults.value.length,
  itemListElement: searchResults.value.map((project, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    url: getProjectCanonicalUrl(project),
    item: {
      '@id': getProjectListingId(project),
    },
  })),
}));
const searchResultsSchema = computed(() => ({
  '@context': 'https://schema.org',
  ...itemList.value,
}));

const fetchSearchResults = async () => {
  try {
    loading.value = true;

    let response;
    if (props.locationSlug && props.parentLocationSlug && props.categorySlug) {
      response = await getEditorialChildLocationNearbySearchResults({
        path: {
          editorialCategorySlug: props.categorySlug,
          parentEditorialLocationSlug: props.parentLocationSlug,
          editorialLocationSlug: props.locationSlug,
        },
      });
    } else if (props.locationSlug && props.categorySlug) {
      response = await getEditorialLocationNearbySearchResults({
        path: {
          editorialCategorySlug: props.categorySlug,
          editorialLocationSlug: props.locationSlug,
        },
      });
    } else {
      searchResults.value = [];
      return;
    }

    if (response.response?.ok && response.data) {
      searchResults.value = response.data.items;
    } else {
      searchResults.value = [];
    }
  } catch (error) {
    console.error('Error fetching editorial nearby search results:', error);
    searchResults.value = [];
  } finally {
    loading.value = false;
  }
};

useSsgState({
  key: `editorial-nearby-results:${props.categorySlug}/${props.parentLocationSlug}/${props.locationSlug}`,
  refs: { loading, searchResults },
  isLoaded: () => !loading.value,
  load: fetchSearchResults,
});

watch(
  () => [props.categorySlug, props.parentLocationSlug, props.locationSlug],
  () => {
    void fetchSearchResults();
  }
);

useHead({
  script: (): ResolvableScript[] => [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify(searchResultsSchema.value),
    },
    ...projectJsonLd.value.map((schema): ResolvableScript => ({
      type: 'application/ld+json',
      innerHTML: schema,
    })),
  ],
});
</script>

<template>
  <section>
    <SectionHeader>{{ t('views.editorial.nearby-search-results.title') }}</SectionHeader>

    <div v-if="loading" class="flex justify-center py-8">
      <div class="text-gray-500">{{ t('views.editorial.nearby-search-results.loading') }}</div>
    </div>

    <div v-else-if="hasResults" class="space-y-6">
      <ul class="xs:grid-cols-2 grid grid-cols-1 gap-6 p-1 md:grid-cols-3 lg:grid-cols-4" role="list">
        <template v-for="(project, index) in searchResults" :key="project.id">
          <SearchResult :index="index" :price-type-id="priceTypeId" :project="project" />
        </template>
      </ul>
    </div>

    <div v-else class="py-8 text-center text-gray-500">
      {{ t('views.editorial.nearby-search-results.no-results') }}
    </div>
  </section>
</template>

<style lang="css" scoped></style>
